//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse} from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import PaymentTransactionModel from './paymentTransaction.model';

/**
 * @service PaymentTransactionService
 * @description Service for managing vendor payment transactions within the Material Management System (MMS).
 * This service handles the financial interactions between the business and its vendors, specifically focusing on
 * recording payments, updating dues, and retrieving transaction history (registers).
 *
 * **Key Responsibilities:**
 * 1. **Payment Recording**: Adding new payment transactions for vendors.
 * 2. **Dues Management**: Updating payment status for outstanding vendor dues.
 * 3. **Transaction History**: Retrieving the payment transaction register for specific farm sites and businesses.
 * 4. **Security/Role Management**: Sanitizing request payloads and query parameters based on the logged-in user's role to ensure data isolation.
 *
 * **Data Flow:**
 * - **Add/Update**: The component sends a `PaymentTransactionModel`. The service checks `localStorage.loginPersonType`.
 *   If the user is not an 'EMPLOYEE', the `business_id` is stripped from the payload to prevent unauthorized cross-business modifications.
 * - **Retrieval**: When fetching the register, the service dynamically constructs the query string. Employees can query specific business IDs,
 *   while other roles are restricted to their implicit context.
 *
 * @feature 1. Pay vendor dues.
 * @feature 2. Add new vendor payment transactions.
 * @feature 3. View payment transaction registers.
 * @feature 4. Role-based security for payload and query sanitization.
 *
 * @architecture
 * Extends base Service<PaymentTransactionModel>.
 * Although the base service URL is defined as `/mms/paymenttransactions/`, specific methods override this to hit
 * `/mms/vendorpaymentdues/` or `/mms/vendorpaymenttransactions/` endpoints as required by the backend microservices structure.
 *
 * @service-endpoint /mms/paymenttransactions/ (Base), /mms/vendorpaymentdues/, /mms/vendorpaymenttransactions/
 * @service-provider root (singleton, application-wide)
 *
 * @local-storage-keys
 * - loginPersonType: Used to determine if the user is an 'EMPLOYEE', affecting payload sanitization and query construction.
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class PaymentTransactionService extends Service<PaymentTransactionModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the base API endpoint path.
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/mms/paymenttransactions/');
  }

  /**
   * @method updateVendorDuesPayment
   * @async
   * @description Update the payment status for vendor dues.
   *
   * This method sends a request to pay off existing dues. It includes security logic to remove
   * the `business_id` from the payload if the user is not an 'EMPLOYEE', ensuring that non-employees
   * cannot manipulate data for businesses they do not belong to.
   *
   * @param {PaymentTransactionModel} paymentTransactionData - The model containing payment details and due identification.
   * @returns {Promise<PaymentTransactionModel>} Promise resolving to the updated transaction model.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Modifies `paymentTransactionData` (removes `business_id`) if user is not an employee.
   * - Initiates an HTTP POST request to `/mms/vendorpaymentdues/payDue`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public updateVendorDuesPayment(paymentTransactionData: PaymentTransactionModel) {
     if (localStorage.loginPersonType != 'EMPLOYEE') {
      delete paymentTransactionData.business_id;
    }
    return new Promise<PaymentTransactionModel>((resolve, reject) => {
      const request = this.httpClient.post<PaymentTransactionModel>(`${this.baseUrl}/mms/vendorpaymentdues/payDue`, paymentTransactionData, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method paymentTransactionRegisterData
   * @async
   * @description Retrieve the payment transaction register (history).
   *
   * Fetches a list of payment transactions filtered by farm site and optionally by business ID.
   *
   * **Role-Based Logic:**
   * - If `loginPersonType` is 'EMPLOYEE', the `business_id` query parameter is included.
   * - Otherwise, `business_id` is omitted, relying on the backend to infer the context from the user's token.
   *
   * @param {string} businessId - The unique identifier of the business (used only for employees).
   * @param {string | number} farmSiteId - The unique identifier of the farm site to filter transactions.
   * @returns {Promise<Array<PaymentTransactionModel>>} Promise resolving to an array of payment transactions.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request to `/mms/vendorpaymenttransactions/paymentTransactionRegister`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public paymentTransactionRegisterData(businessId: string, farmSiteId: string | number) {
    let queryPath = '';
    if (localStorage.loginPersonType == 'EMPLOYEE') {
      queryPath = `business_id=${businessId}&`;
    } else {
      queryPath = ``;
    }
    return new Promise<Array<PaymentTransactionModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<PaymentTransactionModel>>(`${this.baseUrl}/mms/vendorpaymenttransactions/paymentTransactionRegister?${queryPath}farm_site_id=${farmSiteId}&updatedAt=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method addPaymentTransaction
   * @async
   * @description Record a new vendor payment transaction.
   *
   * Adds a new payment record to the system. Similar to `updateVendorDuesPayment`, this method
   * sanitizes the payload by removing `business_id` for non-employee users to enforce security boundaries.
   *
   * @param {PaymentTransactionModel} paymentTransaction - The model containing details of the new payment.
   * @returns {Promise<PaymentTransactionModel>} Promise resolving to the created transaction model.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Modifies `paymentTransaction` (removes `business_id`) if user is not an employee.
   * - Initiates an HTTP POST request to `/mms/vendorpaymenttransactions/addVendorPaymentTransaction`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public addPaymentTransaction(paymentTransaction: PaymentTransactionModel) {
    if (localStorage.loginPersonType != 'EMPLOYEE') {
      delete paymentTransaction.business_id;
    }
    return new Promise<PaymentTransactionModel>((resolve, reject) => {
      const request = this.httpClient.post<PaymentTransactionModel>(`${this.baseUrl}/mms/vendorpaymenttransactions/addVendorPaymentTransaction`, paymentTransaction, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
